// src/components/dashboard/MissingServices.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const MissingServices = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme")
      );
    };
    checkTheme();
    const obs = new MutationObserver(checkTheme);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Filter States
  const [staff, setStaff] = useState("");
  const [quickRange, setQuickRange] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [nonBillable, setNonBillable] = useState("");
  const [auditReady, setAuditReady] = useState(false);

  // Pagination & Dataset States (Matching Screenshot Rows)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [missingRows] = useState([
    { id: 4, staff: "Atif", client: "asad asass", date: "N/A", code: "NEZ-2300", note: "Missing CNS" },
    { id: 24, staff: "dsaf", client: "Asif Ali", date: "N/A", code: "NEZ-9006", note: "Missing CNS" },
    { id: 6, staff: "TruEvv", client: "asad asass", date: "Jul 24, 2026", code: "NEZ-2300", note: "Missing CNS" },
    { id: 9, staff: "TruEvv", client: "asad asass", date: "Jul 14, 2026", code: "NEZ-2300", note: "Missing CNS" },
    { id: 16, staff: "TruEvv", client: "asad asass", date: "Jul 13, 2026", code: "NEZ-8000", note: "Missing CNS" },
    { id: 8, staff: "TruEvv", client: "asad asass", date: "Jul 1, 2026", code: "NEZ-2300", note: "Missing CNS" },
    { id: 7, staff: "TruEvv", client: "asad asass", date: "N/A", code: "NEZ-2300", note: "Missing CNS" },
    { id: 11, staff: "TruEvv", client: "asad asass", date: "N/A", code: "NEZ-2300", note: "Missing CNS" },
  ]);

  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryText = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryText = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleClear = () => {
    setStaff("");
    setQuickRange("");
    setDateFrom("");
    setDateTo("");
    setNonBillable("");
    setAuditReady(false);
  };

  const filteredRows = missingRows.filter((r) => {
    return staff === "" || r.staff.toLowerCase().includes(staff.toLowerCase());
  });

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryText,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg },
          "&.Mui-selected": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg, color: tealButton, fontWeight: 700 },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. Header Banner */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ErrorOutlineRoundedIcon sx={{ fontSize: "1.75rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, color: primaryText }}>
              Service Lines Missing Notes
            </Typography>
            <Chip
              label="Exceptions"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 500, mt: 0.3 }}>
            Identify service lines without associated notes
          </Typography>
        </Box>
      </Box>

      {/* 2. Filter Section */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SearchIcon sx={{ color: labelTeal, fontSize: "1.3rem" }} />
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryText }}>
            Filter Service Lines
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          {/* Staff Select */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Staff</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={staff}
              onChange={(e) => setStaff(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select staff...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="">All Staff</MenuItem>
              <MenuItem value="Atif">Atif</MenuItem>
              <MenuItem value="dsaf">dsaf</MenuItem>
              <MenuItem value="TruEvv">TruEvv</MenuItem>
            </Select>
          </Box>

          {/* Quick Range */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Quick Range</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={quickRange}
              onChange={(e) => setQuickRange(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select date range...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="Today">Today</MenuItem>
              <MenuItem value="This Week">This Week</MenuItem>
              <MenuItem value="This Month">This Month</MenuItem>
            </Select>
          </Box>

          {/* Date of Session */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Date of Session</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
              <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 600 }}>to</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
            </Box>
          </Box>

          {/* Include Non-Billable Codes */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Include Non-Billable Codes</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={nonBillable}
              onChange={(e) => setNonBillable(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select Option</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* Audit Ready Checkbox */}
        <Box sx={{ mt: 2.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={auditReady}
                onChange={(e) => setAuditReady(e.target.checked)}
                sx={{ color: tealButton, "&.Mui-checked": { color: tealMain } }}
              />
            }
            label={<Typography sx={{ fontSize: "0.86rem", fontWeight: 600, color: primaryText }}>Audit Ready</Typography>}
          />
        </Box>

        {/* Search & Clear Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3.5 }}>
          <Button
            variant="contained"
            sx={{ height: "40px", backgroundColor: tealMain, color: "#FFFFFF", fontWeight: 700, fontSize: "0.84rem", borderRadius: "8px", px: 3, textTransform: "none", boxShadow: "none", "&:hover": { backgroundColor: tealButton } }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<DeleteOutlineRoundedIcon />}
            sx={{ height: "40px", borderColor: tealButton, color: tealButton, fontWeight: 700, fontSize: "0.84rem", borderRadius: "8px", px: 2.8, textTransform: "none" }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. Results Table Container */}
      <Box sx={{ backgroundColor: cardBg, borderRadius: "16px", border: `1px solid ${softCardBorder}`, overflow: "hidden" }}>
        <TableContainer sx={{ minHeight: "350px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { color: primaryText, fontWeight: 800, fontSize: "0.86rem", backgroundColor: isDark ? "#162238" : "#F8FAFC", py: 2, px: 3 } }}>
                <TableCell>Staff</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 10, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryText, fontSize: "0.9rem", fontWeight: 600 }}>No rows</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow key={row.id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.03)" } }}>
                    <TableCell sx={{ fontWeight: 600, color: tealButton, cursor: "pointer", textDecoration: "underline" }}>{row.staff}</TableCell>
                    <TableCell sx={{ color: primaryText, fontWeight: 600 }}>{row.client}</TableCell>
                    <TableCell sx={{ color: secondaryText }}>{row.date}</TableCell>
                    <TableCell sx={{ color: primaryText, fontWeight: 600 }}>{row.code}</TableCell>
                    <TableCell sx={{ color: primaryText }}>{row.note}</TableCell>
                    <TableCell sx={{ color: secondaryText, fontWeight: 600 }}>{row.id}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ borderTop: `1px solid ${softCardBorder}`, color: secondaryText }}
        />
      </Box>
    </Box>
  );
};

export default MissingServices;